'use client'

import { MinusIcon, PlusIcon } from '@heroicons/react/24/outline'
import { startTransition } from 'react'

import { RemoveLineItemButton } from '@src/app/[country]/[lang]/checkout/modules/RemoveLineItemButton'
import { Spinner } from '@src/components/Spinner/Spinner'
import { useCart } from '@src/hooks/useCart'
import { useErrors } from '@src/hooks/useErrors'
import { useLoadings } from '@src/hooks/useLoadings'
import { ServerActions } from '@src/services/server-actions.service'
import { trackUpdateQuantity } from '@src/utils/analytics.util'
import { ErrorCodes } from '@src/utils/constants.util'
import { createJSendFailure } from '@src/utils/jsend.util'
import { log } from '@src/utils/log.util'
import { getMaxQuantity } from '@src/utils/product.util'
import { cn } from '@src/utils/tailwind'

const ERROR_DURATION = 3000

export interface QuantitySelectorProps {
  lineItem?: Commercetools.LineItem
  optimisticQuantity: number
  setOptimisticQuantity: (quantity: number) => void
}

export const LineItemSettings = (props: QuantitySelectorProps) => {
  const { cart, setCart } = useCart()
  const { setError, ...errors } = useErrors()
  const { loadings, setLoading } = useLoadings()

  const { lineItem, optimisticQuantity, setOptimisticQuantity } = props

  const productType = lineItem?.productType?.obj?.key as keyof ProductTypeMeta
  const maxQuantity = getMaxQuantity(productType)
  const sku = lineItem?.variant?.sku as string
  const isRemoving =
    // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
    loadings.removeLineItems?.[sku] || optimisticQuantity === 0

  const isQuantityError =
    errors?.code === ErrorCodes.InvalidQuantity && errors.additionalDetails?.skus?.includes(sku)

  if (!lineItem || !cart) return null

  const changeLineItemQuantity = async (quantity: number) => {
    const prev = optimisticQuantity
    if (quantity > maxQuantity) {
      setError(
        createJSendFailure({
          code: 'invalid-quantity',
        }),
        {
          duration: ERROR_DURATION,
        }
      )
      return
    }

    startTransition(async () => {
      setOptimisticQuantity(quantity)
    })

    try {
      const cart = await ServerActions.serial.changeLineItems([
        { lineItemId: lineItem.id, quantity },
      ])
      setCart(cart)
      const newLineItem = cart?.lineItems?.find((item) => item.variant?.sku === sku)
      trackUpdateQuantity(newLineItem)
    } catch (error: any) {
      log.error(`Error changing line item in cart - ${error?.message}`, error)
      setError(error, { duration: undefined })
      // revert on error
      startTransition(async () => {
        setOptimisticQuantity(prev)
      })
    } finally {
      setLoading({
        changeLineItems: {
          [sku]: false,
        },
      })
    }
  }

  return (
    <div className="flex gap-2">
      {maxQuantity > 1 && (
        <div
          className={cn([
            'flex font-medium',
            '*:size-[44px] *:border *:border-bodi-gray-butters',
            '*:flex *:items-center *:justify-center',
            { 'outline outline-2 rounded-lg outline-bodi-red-error': isQuantityError },
          ])}
        >
          <button
            data-testid="quantityDecrease"
            onClick={() => {
              void changeLineItemQuantity(optimisticQuantity - 1)
            }}
            disabled={isRemoving}
            className="rounded-l-lg !border-r-0 hover:bg-bodi-gray-pearl disabled:cursor-not-allowed disabled:bg-bodi-gray-pearl"
          >
            <MinusIcon className="size-4" />
          </button>
          <div
            className={cn('!hover:bg-white', {
              'bg-bodi-gray-pearl': isRemoving,
            })}
          >
            {isRemoving ? <Spinner size="smaller" /> : optimisticQuantity}
          </div>
          <button
            data-testid="quantityIncrease"
            onClick={() => {
              void changeLineItemQuantity(optimisticQuantity + 1)
            }}
            disabled={isRemoving}
            className="rounded-r-lg !border-l-0 hover:bg-bodi-gray-pearl disabled:cursor-not-allowed disabled:bg-bodi-gray-pearl"
          >
            <PlusIcon className="size-4" />
          </button>
        </div>
      )}

      {optimisticQuantity > 0 && (
        <RemoveLineItemButton
          shouldRedirect={false}
          className="ml-0 *:!mb-0 *:!size-5 *:!translate-y-0"
          lineItem={lineItem}
        />
      )}
    </div>
  )
}
